/**
 * Collection bundle entry.
 *
 * Self-initializes only when the page provides a filter grid mount point
 * (`[data-collection-filter-grid]`), so it stays inert until the collection
 * template opts in. Reads page context + filter options exposed from Liquid via
 * `Merchology.CollectionConfig` / `Merchology.CollectionFilterOptions`
 * (see snippets/collection-filter-config.liquid) and the existing `DISCOUNTS` global.
 */

import { createCollectionFilterEngine } from '../Collection/index';
import { CollectionView } from '../Collection/CollectionView';
import { FilterSidebar, SidebarGroup } from '../Collection/FilterSidebar';
import { mergeSelections, readLegacyFilterUrl } from '../Collection/LegacyFilterUrl';
import { FacetGroup, PageContext } from '../Collection/types';

/**
 * `Merchology` is created by merchology-environment.liquid as a top-level `const` (a
 * lexical global, NOT a window property), so reference it directly and fall back to window.
 */
function merchologyGlobal(): any {
  try {
    return (Merchology as any) || (window as any).Merchology || {};
  } catch (e) {
    return (window as any).Merchology || {};
  }
}

function readContext(): PageContext | null {
  const M = merchologyGlobal();
  const cfg = M.CollectionConfig as PageContext | undefined;
  if (!cfg || !cfg.collectionHandle) return null;
  if (!cfg.discounts && (window as any).DISCOUNTS) cfg.discounts = (window as any).DISCOUNTS;
  return cfg;
}

function readGroups(): SidebarGroup[] {
  const groups = merchologyGlobal().CollectionFilterOptions;
  return Array.isArray(groups) ? (groups as SidebarGroup[]) : [];
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
}

function currentSort(): string | undefined {
  const m = window.location.search.match(/[?&]sort_by=([^&]+)/);
  return m ? safeDecode(m[1]) : undefined;
}

function closestEl(el: HTMLElement | null, selector: string): HTMLElement | null {
  let node: HTMLElement | null = el;
  while (node && node.nodeType === 1) {
    if (node.matches && node.matches(selector)) return node;
    node = node.parentElement;
  }
  return null;
}

const SWATCH_SLICK_OPTS = {
  slidesToShow: 7,
  slidesToScroll: 6,
  speed: 500,
  infinite: false,
  arrows: true,
  cssEase: 'ease-in-out',
  prevArrow: "<span class='arrow_carrot-left coll-prevArrow'></span>",
  nextArrow: "<span class='arrow_carrot-right coll-nextArrow'></span>",
  responsive: [
    { breakpoint: 767, settings: { swipeToSlide: true, slidesToShow: 4, slidesToScroll: 3 } },
    { breakpoint: 480, settings: { swipeToSlide: true, slidesToShow: 4, slidesToScroll: 3 } },
  ],
};

function initCardWidgets(cards: HTMLElement[]): void {
  const jq = (window as any).jQuery || (window as any).$;
  if (!jq) return;
  for (let i = 0; i < cards.length; i++) {
    const $card = jq(cards[i]);
    if (jq.fn && jq.fn.slick) {
      const $slider = $card.find('.colorChoiceSlider-coll');
      if ($slider.length && $slider.children().length && !$slider.hasClass('slick-initialized')) {
        try {
          $slider.slick(SWATCH_SLICK_OPTS);
        } catch (e) {
          /* leave the swatches static rather than throwing out of card init */
        }
      }
    }
    if (jq.fn && jq.fn.tooltip) $card.find('[data-toggle="tooltip"]').tooltip();
  }
  const compare = (window as any).MerchologyCompare;
  if (compare && typeof compare.refresh === 'function') compare.refresh();
}

function formatCount(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function markSort(list: HTMLElement, value?: string): void {
  const items = list.querySelectorAll('li[data-value]');
  for (let i = 0; i < items.length; i++) {
    const li = items[i] as HTMLElement;
    if (value && li.getAttribute('data-value') === value) li.classList.add('selected');
    else li.classList.remove('selected');
  }
}

function init(): void {
  const gridEl = document.querySelector('[data-collection-filter-grid]') as HTMLElement | null;
  if (!gridEl) return; // template hasn't opted in yet

  const ctx = readContext();
  if (!ctx) {
    if (typeof console !== 'undefined') console.warn('CollectionConfig missing; filter grid inactive');
    return;
  }

  const engine = createCollectionFilterEngine({ handle: ctx.collectionHandle });
  const groups = readGroups();
  engine.loadFromUrl(window.location.search);

  // Legacy tag-filtered links (/collections/<handle>/feature-packable+product_type-jackets)
  // are still live on the client's pages. Translate the tag segment into a selection before
  // anything reads one, so the sidebar, the chips and the grid all agree on what's applied,
  // and rewrite the address bar to the engine's own query-string form (syncUrl below).
  // Leaving the tag segment in the path would have every later pushState build on a path the
  // engine doesn't filter by, so the next click would silently drop the link's filters.
  const legacy = readLegacyFilterUrl({
    pathname: window.location.pathname,
    legacyTags: ctx.legacyTags,
    groups: groups,
  });
  if (legacy) engine.setSelection(mergeSelections(engine.getSelection(), legacy.selection));
  const basePath = legacy ? legacy.canonicalPath : window.location.pathname;

  let sortValue = currentSort();
  let sidebar: FilterSidebar | null = null;

  const countEl = document.querySelector('[data-collection-filter-count]') as HTMLElement | null;
  // Write the count into the inner label span, never the count element itself: product-compare
  // appends the sticky compare tray as a child of .collection-product-count, and setting the
  // outer element's textContent would destroy it.
  const countLabelEl =
    (countEl && (countEl.querySelector('[data-collection-filter-count-label]') as HTMLElement | null)) ||
    countEl;
  const totalAttr = countEl ? countEl.getAttribute('data-collection-filter-total') : null;
  const initialCountText = totalAttr
    ? totalAttr + ' Products'
    : (countLabelEl ? countLabelEl.textContent || '' : '');

  // The last count we could actually stand behind. Held on screen while the next one is
  // still being worked out, so the label never regresses to a placeholder.
  let settledCountText = initialCountText;

  function setCount(text: string): void {
    settledCountText = text;
    if (countLabelEl) countLabelEl.textContent = text;
    if (countEl) countEl.classList.remove('sf-count-pending');
  }

  function setCountPending(): void {
    if (countLabelEl) countLabelEl.textContent = settledCountText;
    if (countEl) countEl.classList.add('sf-count-pending');
  }

  const view = new CollectionView({
    engine,
    ctx,
    gridEl,
    statusEl: document.querySelector('[data-collection-filter-status]') as HTMLElement | null,
    loadMoreEl: document.querySelector('[data-collection-filter-loadmore]') as HTMLElement | null,
    sortValue,
    onFacets: (facets: FacetGroup[]) => {
      if (sidebar) sidebar.updateCounts(facets);
    },
    // Count precedence, most to least authoritative: an exhausted grid, the background
    // count pass (exact under client refinement), then the API's own facet total (exact
    // without refinement — see CollectionQuery.totalFromFacets). Never derive it from a
    // selected option's facet count: tag groups carry no count, so that silently ignored
    // every tag filter. And never from the rendered card count either — "48+" just
    // reports the page size, which tells a shopper nothing.
    //
    // When none of the three can answer yet, hold the last real number and mark the label
    // pending rather than flashing a placeholder; the count pass replaces it a moment later.
    onPage: (info) => {
      if (!countEl || !countLabelEl) return;
      const active = Object.keys(engine.getSelection()).length > 0;
      if (!active) {
        setCount(initialCountText);
        return;
      }
      if (!info.hasNext) {
        setCount(formatCount(info.rendered) + ' Products');
      } else if (info.exactTotal !== null) {
        setCount(formatCount(info.exactTotal) + (info.exactTotalCapped ? '+' : '') + ' Products');
      } else if (info.apiTotal !== null) {
        setCount((info.apiTotalCapped ? '5,000+' : formatCount(info.apiTotal)) + ' Products');
      } else {
        setCountPending();
      }
    },
    onCardsRendered: (cards) => initCardWidgets(cards),
  });

  const sidebarEl = document.querySelector('[data-collection-filter-sidebar]') as HTMLElement | null;
  if (sidebarEl) {
    sidebar = new FilterSidebar({
      container: sidebarEl,
      groups: groups,
      engine,
      chipsEl: document.querySelector('[data-collection-filter-chips]') as HTMLElement | null,
      onApply: () => {
        syncUrl();
        setCountPending();
        view.applySelection();
      },
    });
  }

  // Params the engine owns and rewrites; everything else (view, utm_*, gclid, …) is
  // preserved so a filter interaction never strips attribution or app params.
  const MANAGED = /^(filter\.|sort_by$|page$)/;

  function syncUrl(replace?: boolean): void {
    const parts: string[] = [];
    window.location.search.replace(/^\?/, '').split('&').forEach((p) => {
      if (!p) return;
      const key = safeDecode(p.split('=')[0]);
      if (!MANAGED.test(key)) parts.push(p);
    });
    const qs = engine.toUrl(sortValue);
    if (qs) parts.push(qs);
    const url = basePath + (parts.length ? '?' + parts.join('&') : '');
    if (!window.history) return;
    // The legacy rewrite replaces: that URL shouldn't sit in history as a step Back returns
    // to, since coming back to it would just translate and rewrite it again.
    if (replace && window.history.replaceState) window.history.replaceState({ sf: true }, '', url);
    else if (window.history.pushState) window.history.pushState({ sf: true }, '', url);
  }

  if (legacy) syncUrl(true);

  window.addEventListener('popstate', () => {
    engine.loadFromUrl(window.location.search);
    sortValue = currentSort();
    const sl = document.getElementById('sort-by-list');
    if (sl) markSort(sl, sortValue);
    if (sidebar) sidebar.rerender();
    setCountPending();
    view.applySelection();
  });

  // AJAX sort. This deferred bundle binds before the theme's document.ready binds its own
  // `.product-sorting-trigger` handler, so stopImmediatePropagation below is what stops that
  // handler from also firing and toggling the dropdown straight back shut.
  const sortWrap = document.querySelector('[data-collection-filter-sort]') as HTMLElement | null;
  const sortMenu = document.getElementById('sort-by-list');
  if (sortMenu) markSort(sortMenu, sortValue);
  if (sortWrap && sortMenu) {
    const trigger = sortWrap.querySelector('.product-sorting-trigger') as HTMLElement | null;
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        const show = sortMenu.style.display !== 'block';
        sortMenu.style.display = show ? 'block' : 'none';
        const arrow = trigger.querySelector('.arrow');
        if (arrow) arrow.classList.toggle('arrow-active', show);
      });
    }
    document.addEventListener('click', (e) => {
      if (!sortWrap.contains(e.target as Node)) sortMenu.style.display = 'none';
    });
  }
  document.addEventListener('click', (e) => {
    const li = closestEl(e.target as HTMLElement, '#sort-by-list li[data-value]');
    if (!li) return;
    e.preventDefault();
    const val = li.getAttribute('data-value') || '';
    sortValue = val;
    if (sortMenu) {
      markSort(sortMenu, val);
      sortMenu.style.display = 'none';
    }
    syncUrl();
    view.applySort(val);
  });

  merchologyGlobal().CollectionView = view;

  // The server-rendered pagination nav exists for crawlers only; JS pages via Load More.
  const pager = document.querySelector('[data-collection-filter-pagination]') as HTMLElement | null;
  if (pager) pager.style.display = 'none';

  const hasPageParam = /[?&]page=/.test(window.location.search);
  const selectionActive = Object.keys(engine.getSelection()).length > 0;
  const serverRendered = gridEl.children.length > 0;
  // A legacy URL's server-rendered grid was filtered by Shopify's own path tag filtering,
  // which isn't necessarily what the translated selection means (and is exactly what the
  // dropped legacy values differ on) — re-fetch rather than adopt cards the engine didn't pick.
  if (serverRendered && !legacy && !selectionActive && !sortValue && !hasPageParam) {
    view.hydrate();
  } else {
    view.start();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
